/**
 * Audit event registry — the code-authoritative classification of every event
 * the platform records. Shared between apps/api (which writes the rows and
 * composes their descriptions) and apps/web (which labels and filters them).
 *
 * This file exists because classification used to happen at READ time, in a
 * SQL `LIKE` ladder that was duplicated in TypeScript. Two copies drift, and
 * both copies mis-filed every module added after they were written. One table,
 * consulted by the writer, is the fix.
 *
 * ── Two orthogonal facets, and why the names are confusing ───────────────────
 *
 * `stream` — WHICH LOG this is: audit | security | system | integration | error.
 *            Stored in `audit_events.category` (the column pre-dates this
 *            vocabulary; renaming it would break nothing but buys nothing).
 *            Only `audit` and `security` reach the user-facing feeds.
 *
 * `domain` — WHICH PART OF THE PRODUCT it came from: customer, server, billing,
 *            identity, … Surfaced to the API and UI as `category`, because that
 *            is the query parameter the existing clients already send. Derived
 *            from the action name, never stored.
 *
 * So: DB `category` == stream, API `category` == domain. Both facets are
 * filterable and they are independent — a `security` event has a domain too.
 *
 * ── The action names are frozen ──────────────────────────────────────────────
 *
 * An event's `action` is also its webhook subscription key (see
 * webhook-events.ts). Renaming one silently orphans every webhook subscribed to
 * it, so the existing names stay exactly as they are — including the six that
 * spell failure into the name (`*_failed`). Those keep their names AND carry
 * `outcome: 'failure'`; `outcome` is what you filter and report on.
 *
 * New events should use the `<domain>.<action>` convention (see the fortigate.*
 * and api_key.* entries at the bottom). The pre-existing snake_case names are
 * grandfathered, not a precedent.
 */

/** Which of the five logs an event belongs to. Stored in audit_events.category. */
export const AUDIT_STREAMS = [
  "audit",
  "security",
  "system",
  "integration",
  "error",
] as const;
export type AuditStream = (typeof AUDIT_STREAMS)[number];

/** How much attention the event deserves. */
export const AUDIT_SEVERITIES = [
  "info",
  "notice",
  "warning",
  "error",
  "critical",
] as const;
export type AuditSeverity = (typeof AUDIT_SEVERITIES)[number];

/** Authoritative result of the audited operation. */
export const AUDIT_OUTCOMES = ["success", "failure", "partial"] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

/** The streams whose events are business/security activity a person should see. */
export const USER_FACING_STREAMS: readonly AuditStream[] = [
  "audit",
  "security",
];

// ─────────────────────────────────────────────────────────────────────────────
// Domain derivation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Product areas. The first ten are the historical `AuditCategory` values and
 * are kept verbatim so existing saved filters and API calls keep working; the
 * rest are the modules the old ladder had no case for and therefore dumped into
 * `other` (or, worse, mis-filed — `customer_audit_*` matched `customer%`).
 */
export const AUDIT_DOMAINS = [
  "auth",
  "customer",
  "server",
  "container",
  "jenkins",
  "report",
  "webhook",
  "settings",
  "browser",
  "customer_audit",
  "billing",
  "identity",
  "m365",
  "gdap",
  "service",
  "fortigate",
  "api_key",
  "copilot",
  "other",
] as const;
export type AuditDomain = (typeof AUDIT_DOMAINS)[number];

/** Human labels for the domain filter. */
export const AUDIT_DOMAIN_LABELS: Record<AuditDomain, string> = {
  auth: "Authentication",
  customer: "Customers",
  server: "Servers",
  container: "Containers",
  jenkins: "Jenkins",
  report: "Reports",
  webhook: "Webhooks",
  settings: "Settings",
  browser: "Browser sessions",
  customer_audit: "Customer audits",
  billing: "Billing",
  identity: "Users & roles",
  m365: "Microsoft 365",
  gdap: "GDAP",
  service: "Services catalog",
  fortigate: "FortiGate",
  copilot: "Copilot integrations",
  api_key: "API keys",
  other: "Other",
};

/**
 * Ordered prefix rules, MOST SPECIFIC FIRST. Order is load-bearing:
 * `customer_audit_%` must be tested before `customer%`, which is exactly the
 * bug the old ladder had (every Customer Audits event was filed under
 * Customers). The API builds its SQL CASE from this array, so the database and
 * the browser cannot disagree about what a domain means.
 */
export const AUDIT_DOMAIN_RULES: readonly {
  domain: AuditDomain;
  /** Matched with `startsWith` / SQL `LIKE 'prefix%'`. */
  prefixes: readonly string[];
  /** Matched with equality, for names that carry no usable prefix. */
  exact?: readonly string[];
}[] = [
  { domain: "customer_audit", prefixes: ["customer_audit_"] },
  { domain: "auth", prefixes: ["login", "refresh"], exact: ["logout", "lockout"] },
  { domain: "identity", prefixes: ["user.", "role."] },
  { domain: "billing", prefixes: ["billing."] },
  { domain: "api_key", prefixes: ["api_key.", "settings_api_key_"] },
  { domain: "fortigate", prefixes: ["fortigate."] },
  { domain: "copilot", prefixes: ["copilot."] },
  { domain: "m365", prefixes: ["m365_"] },
  { domain: "gdap", prefixes: ["gdap_"] },
  { domain: "customer", prefixes: ["customer_"] },
  { domain: "server", prefixes: ["server_"] },
  { domain: "container", prefixes: ["container_"] },
  { domain: "jenkins", prefixes: ["jenkins_"] },
  { domain: "report", prefixes: ["report_"] },
  { domain: "service", prefixes: ["service_"] },
  { domain: "webhook", prefixes: ["webhook_"] },
  { domain: "settings", prefixes: ["settings_"] },
  { domain: "browser", prefixes: ["browser_", "browser."] },
];

/** Derive the product area from an action name. Mirrors the generated SQL CASE. */
export function auditDomainOf(action: string): AuditDomain {
  for (const rule of AUDIT_DOMAIN_RULES) {
    if (rule.exact?.includes(action)) return rule.domain;
    if (rule.prefixes.some((p) => action.startsWith(p))) return rule.domain;
  }
  return "other";
}

// ─────────────────────────────────────────────────────────────────────────────
// Event registry
// ─────────────────────────────────────────────────────────────────────────────

/** Classification and phrasing for one action name. */
export interface AuditEventSpec {
  /** Which log it belongs in. Defaults to `audit` when omitted. */
  stream?: AuditStream;
  /** Defaults to `info`. */
  severity?: AuditSeverity;
  /**
   * Verb phrase for the human-readable description, past tense, with no actor:
   * the Actor is its own column in every view and every export, so repeating it
   * in the sentence duplicates identity data for no gain — and one fewer
   * denormalized copy of a person's name is one fewer place to scrub when a
   * data-subject request arrives.
   *
   * Composed by the writer as: "<phrase> <entity>[ (<changes>)][ — <reason>]",
   * e.g. "Updated server web-01 (status: pending → approved)".
   */
  phrase: string;
  /** Set when the action name itself means failure (frozen webhook keys). */
  outcome?: AuditOutcome;
}

/**
 * Every action the platform writes. An action missing from here still records
 * correctly — it falls back to `audit`/`info` and a description derived from the
 * action name — but it will not read as well, so add new events here.
 */
export const AUDIT_EVENT_REGISTRY: Readonly<Record<string, AuditEventSpec>> = {
  // ── Customers ─────────────────────────────────────────────────────────────
  customer_upserted: { phrase: "Saved customer" },
  customer_bulk_upserted: { phrase: "Bulk imported customers" },
  customer_result_emailed: { phrase: "Emailed provisioning result for customer" },
  customer_migrated: { phrase: "Migrated customer", stream: "system" },

  // ── Servers ───────────────────────────────────────────────────────────────
  server_created: { phrase: "Added server" },
  server_updated: { phrase: "Updated server" },
  server_deleted: { phrase: "Deleted server", severity: "notice" },
  server_tag_renamed: { phrase: "Renamed server tag" },
  server_tag_deleted: { phrase: "Deleted server tag", severity: "notice" },
  server_pulseway_script_ran: { phrase: "Ran Pulseway update & security refresh on server" },
  // Periodic collectors, not people. Out of the Activity feed.
  server_refreshed: { phrase: "Refreshed server data", stream: "system" },
  server_pulseway_refreshed: { phrase: "Refreshed Pulseway data for server", stream: "system" },
  server_migrated: { phrase: "Migrated server", stream: "system" },

  // ── Containers ────────────────────────────────────────────────────────────
  container_metadata_updated: { phrase: "Updated container classification for" },
  container_refreshed: { phrase: "Refreshed container inventory", stream: "system" },

  // ── Jenkins ───────────────────────────────────────────────────────────────
  jenkins_build_triggered: { phrase: "Triggered Jenkins build" },
  // A re-sync of a data view, not a business mutation.
  jenkins_update_triggered: { phrase: "Synced Jenkins job data", stream: "system" },

  // ── Reports ───────────────────────────────────────────────────────────────
  report_generated: { phrase: "Generated report" },
  // Data leaving the platform is its own event and its own severity.
  report_exported: { phrase: "Exported report", severity: "notice" },
  report_template_saved: { phrase: "Saved report template" },
  report_template_deleted: { phrase: "Deleted report template", severity: "notice" },

  // ── Services catalog ──────────────────────────────────────────────────────
  service_created: { phrase: "Created service" },
  service_updated: { phrase: "Updated service" },
  service_deleted: { phrase: "Deleted service", severity: "notice" },

  // ── Copilot business integrations (global MCP catalog) ────────────────────
  // Security stream, not the ordinary audit stream. Every one of these changes
  // what data can be placed in front of a language model for EVERY user of the
  // platform, which is a security posture change wearing the clothes of a
  // configuration edit. None of them records a secret VALUE: the replacement
  // events record that a replacement happened and by whom, which is the whole
  // of what an investigation needs and the whole of what it may safely have.
  "copilot.mcp_integration.created": {
    phrase: "Created business integration",
    stream: "security",
    severity: "notice",
  },
  "copilot.mcp_integration.updated": {
    phrase: "Updated business integration",
    stream: "security",
  },
  // Split out of `updated` deliberately. "Somebody edited Monday" and "somebody
  // re-pointed Monday's MCP endpoint" are the same row under one action name,
  // and only one of them is worth waking up for.
  "copilot.mcp_integration.endpoint_changed": {
    phrase: "Changed the MCP endpoint of business integration",
    stream: "security",
    severity: "warning",
  },
  "copilot.mcp_integration.auth_changed": {
    phrase: "Changed the authentication strategy of business integration",
    stream: "security",
    severity: "warning",
  },
  "copilot.mcp_integration.client_id_changed": {
    phrase: "Changed the OAuth client id of business integration",
    stream: "security",
    severity: "warning",
  },
  // Distinct from `client_id_changed` because it records something that
  // happened OUTSIDE this system: an OAuth application now exists in a
  // provider's console, created by this deployment rather than by a person.
  "copilot.mcp_integration.oauth_client_registered": {
    phrase: "Registered an OAuth client with the provider of business integration",
    stream: "security",
    severity: "warning",
  },
  // The FACT only. No previous value, no new value, no length, no prefix.
  "copilot.mcp_integration.secret_replaced": {
    phrase: "Replaced a stored credential for business integration",
    stream: "security",
    severity: "warning",
  },
  "copilot.mcp_integration.allowed_tools_changed": {
    phrase: "Changed the allowed tools of business integration",
    stream: "security",
    severity: "notice",
  },
  "copilot.mcp_integration.enabled": {
    phrase: "Enabled business integration",
    stream: "security",
    severity: "notice",
  },
  "copilot.mcp_integration.disabled": {
    phrase: "Disabled business integration",
    stream: "security",
    severity: "notice",
  },
  "copilot.mcp_integration.deleted": {
    phrase: "Deleted business integration",
    stream: "security",
    severity: "warning",
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  settings_general_updated: { phrase: "Updated general settings" },
  settings_branding_updated: { phrase: "Updated deployment branding" },

  // ── Webhooks ──────────────────────────────────────────────────────────────
  webhook_created: { phrase: "Created webhook" },
  webhook_updated: { phrase: "Updated webhook" },
  webhook_deleted: { phrase: "Deleted webhook", severity: "notice" },
  webhook_enabled: { phrase: "Enabled webhook" },
  webhook_disabled: { phrase: "Disabled webhook", severity: "notice" },
  webhook_tested: { phrase: "Sent a test delivery for webhook" },
  webhook_replayed: { phrase: "Replayed webhook delivery" },

  // ── Browser sessions ──────────────────────────────────────────────────────
  browser_launch_request_created: { phrase: "Requested a browser session for" },
  // NOTE: browser_session_viewed is deliberately absent. It was a READ, the
  // write is gone, and a registry entry for an action nothing emits makes the
  // catalogue look more complete than it is (see audit-registry-drift.test.ts).
  browser_session_close_requested: { phrase: "Requested close of browser session" },
  "browser.session_relaunched": { phrase: "Reopened the browser for session" },
  // Emitted since the partnership step shipped, but never registered — it fell
  // into the `other` bucket unclassified. Registered here under the dotted
  // convention; it was never a webhook subscription key, so nothing is orphaned.
  "browser.partnership_requested": { phrase: "Opened the reseller invitation for session" },
  // Agent lifecycle telemetry, reported by the workstation agent.
  browser_launch_consumed: { phrase: "Browser launch consumed for session", stream: "system" },
  browser_agent_active: { phrase: "Browser agent reported active for session", stream: "system" },
  browser_agent_closed: { phrase: "Browser agent reported closed for session", stream: "system" },
  browser_agent_failed: {
    phrase: "Browser agent reported failure for session",
    stream: "system",
    severity: "error",
    outcome: "failure",
  },

  // ── Microsoft 365 onboarding ──────────────────────────────────────────────
  m365_provision_requested: { phrase: "Requested M365 integration provisioning for" },
  m365_provision_started: { phrase: "Started M365 integration provisioning for" },
  m365_provision_completed: { phrase: "Completed M365 integration provisioning for" },
  m365_provision_failed: {
    phrase: "M365 integration provisioning failed for",
    severity: "error",
    outcome: "failure",
  },
  m365_hudu_saved: { phrase: "Saved M365 integration credentials to Hudu for" },

  // ── GDAP ──────────────────────────────────────────────────────────────────
  gdap_started: { phrase: "Started GDAP automation for" },
  gdap_relationship_created: { phrase: "Created GDAP relationship for" },
  gdap_relationship_locked: { phrase: "Locked GDAP relationship for" },
  gdap_approval_opened: { phrase: "Opened GDAP approval for" },
  gdap_relationship_activated: { phrase: "Activated GDAP relationship for" },
  gdap_access_assignment_created: { phrase: "Created GDAP access assignment for" },
  gdap_failed: { phrase: "GDAP automation failed for", severity: "error", outcome: "failure" },

  // ── Customer audits ───────────────────────────────────────────────────────
  customer_audit_batch_started: { phrase: "Started customer audit batch" },
  customer_audit_run_queued: { phrase: "Queued customer audit for" },
  customer_audit_run_succeeded: { phrase: "Completed customer audit for" },
  customer_audit_run_failed: {
    phrase: "Customer audit failed for",
    severity: "error",
    outcome: "failure",
  },
  customer_audit_run_retried: { phrase: "Retried customer audit for" },
  customer_audit_report_emailed: { phrase: "Emailed customer audit report for" },
  customer_audit_artifact_downloaded: {
    phrase: "Downloaded customer audit report for",
    severity: "notice",
  },
  customer_audit_schedule_updated: { phrase: "Updated customer audit schedule" },
  customer_audit_subscription_updated: { phrase: "Updated customer audit subscription for" },
  customer_audit_schedule_fired: {
    phrase: "Customer audit schedule fired",
    stream: "system",
  },

  // ── Billing ───────────────────────────────────────────────────────────────
  "billing.upload.staged": { phrase: "Staged billing upload" },
  "billing.upload.approved": { phrase: "Approved billing upload" },
  "billing.upload.discarded": { phrase: "Discarded billing upload", severity: "notice" },
  "billing.mapping.created": { phrase: "Created billing SKU mapping" },
  "billing.mapping.deleted": { phrase: "Deleted billing SKU mapping", severity: "notice" },
  // Maintaining the catalogue by hand rather than through a price list. Both
  // are 'notice': the catalogue is what every future invoice is priced from, so
  // a change to it made outside the reviewed import path is worth reading.
  "billing.sku.created": { phrase: "Created billing catalogue item", severity: "notice" },
  "billing.sku.updated": { phrase: "Edited billing catalogue item", severity: "notice" },
  "billing.sku_import.uploaded": { phrase: "Uploaded billing price list" },
  "billing.sku_import.applied": { phrase: "Applied billing price list" },
  "billing.sku_import.discarded": { phrase: "Discarded billing price list", severity: "notice" },
  "billing.sku_import.rolled_back": {
    phrase: "Rolled back billing price list",
    severity: "notice",
  },
  "billing.run.created": { phrase: "Created billing run" },
  "billing.run.validated": { phrase: "Validated billing run" },
  "billing.run.exported": { phrase: "Exported billing run", severity: "notice" },
  "billing.run.discarded": { phrase: "Discarded billing run", severity: "notice" },
  "billing.snapshot.requested": { phrase: "Requested billing snapshot" },
  "billing.snapshot.closed": { phrase: "Closed billing snapshot" },
  "billing.snapshot.unsealed": { phrase: "Unsealed billing snapshot", severity: "notice" },
  "billing.snapshot.failed": {
    phrase: "Billing snapshot failed",
    severity: "error",
    outcome: "failure",
  },
  "billing.license_names.rebuilt": { phrase: "Rebuilt billing licence names" },
  // Worker queue lifecycle.
  "billing.snapshot.claimed": { phrase: "Claimed billing snapshot job", stream: "system" },
  "billing.snapshot.recovered": { phrase: "Recovered billing snapshot job", stream: "system" },

  // ── Identity: users ───────────────────────────────────────────────────────
  // Business-significant AND security-significant. ONE row, filed as security;
  // both the Activity and Security views surface it rather than double-writing.
  "user.invite": { phrase: "Invited user", stream: "security" },
  "user.invite.email": { phrase: "Sent invitation email to user", stream: "security" },
  "user.update": { phrase: "Updated user", stream: "security" },
  "user.status.update": { phrase: "Changed status of user", stream: "security", severity: "notice" },
  "user.delete": { phrase: "Deleted user", stream: "security", severity: "notice" },
  "user.role.assign": { phrase: "Assigned a role to user", stream: "security", severity: "notice" },
  "user.role.remove": { phrase: "Removed a role from user", stream: "security", severity: "notice" },
  "user.session.revoke": { phrase: "Revoked a session of user", stream: "security", severity: "notice" },
  "user.sessions.revoke_all": {
    phrase: "Revoked all sessions of user",
    stream: "security",
    severity: "notice",
  },
  "user.bootstrap": { phrase: "Provisioned user on first sign-in", stream: "security" },

  // ── Identity: roles ───────────────────────────────────────────────────────
  "role.create": { phrase: "Created role", stream: "security" },
  "role.update": { phrase: "Updated role", stream: "security" },
  "role.delete": { phrase: "Deleted role", stream: "security", severity: "notice" },
  "role.permissions.set": {
    phrase: "Changed permissions of role",
    stream: "security",
    severity: "notice",
  },
  "role.grants.set": {
    phrase: "Changed module access of role",
    stream: "security",
    severity: "notice",
  },

  // ── API keys (new — see api_key.* naming convention) ──────────────────────
  "api_key.created": { phrase: "Created API key", stream: "security", severity: "notice" },
  "api_key.revoked": { phrase: "Revoked API key", stream: "security", severity: "notice" },
  "api_key.rotated": { phrase: "Rotated API key", stream: "security", severity: "notice" },
  "api_key.updated": { phrase: "Updated API key", stream: "security" },

  // ── FortiGate (new) ───────────────────────────────────────────────────────
  "fortigate.note.saved": { phrase: "Saved FortiGate device note for" },
  "fortigate.note.deleted": { phrase: "Deleted FortiGate device note for", severity: "notice" },
  "fortigate.schedule.updated": { phrase: "Updated FortiGate monitor settings" },
  "fortigate.api.checked": { phrase: "Ran a FortiGate API check against" },
  "fortigate.report.sent": { phrase: "Sent the FortiGate monitor report" },
  "fortigate.sweep.started": { phrase: "Started a FortiGate fleet sweep" },

  // ── Authentication (login_audit — classified here for the unified view) ────
  login_success: { phrase: "Signed in", stream: "security" },
  login_failure: {
    phrase: "Failed to sign in",
    stream: "security",
    severity: "warning",
    outcome: "failure",
  },
  logout: { phrase: "Signed out", stream: "security" },
  lockout: {
    phrase: "Account locked out",
    stream: "security",
    severity: "warning",
    outcome: "failure",
  },
  refresh: { phrase: "Refreshed session", stream: "system" },
  refresh_reuse: {
    phrase: "Refresh token reuse detected",
    stream: "security",
    severity: "critical",
    outcome: "failure",
  },
};

/** Look up an action's spec, or undefined when it is not registered. */
export function auditEventSpec(action: string): AuditEventSpec | undefined {
  return AUDIT_EVENT_REGISTRY[action];
}

/** The stream an action belongs to (`audit` for anything unregistered). */
export function auditStreamOf(action: string): AuditStream {
  return AUDIT_EVENT_REGISTRY[action]?.stream ?? "audit";
}
